import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit2,
  Trash2,
  Lock,
  Unlock,
  Phone,
  Eye,
  CheckCircle2,
  X,
  MapPin,
  Router,
  Radio,
  FileText,
  FileSpreadsheet,
  Upload,
  CalendarClock
} from "lucide-react";
import { TundaIsolirDialog } from "../components/common/TundaIsolirDialog";
import { AddPelangganModal } from "../components/common/AddPelangganModal";
import { ImportPelangganModal } from "../components/common/ImportPelangganModal";
import { UpgradeRequestsPanel } from "../components/common/UpgradeRequestsPanel";
import { PelangganFotoPanel } from "../components/common/PelangganFoto";
import { useAuth } from "../context/AuthContext";

export const DataPelangganPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const canAdd = role !== "investor"; // teknisi boleh menambah pelanggan baru
  const canEdit = role === "admin" || role === "finance"; // edit/hapus/isolir khusus admin & finance
  const [pelangganList, setPelangganList] = useState([]);
  const [masterPaket, setMasterPaket] = useState([]);
  const [masterOdp, setMasterOdp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterArea, setFilterArea] = useState("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [tundaCustomer, setTundaCustomer] = useState(null);
  const [notification, setNotification] = useState("");

  // Form State
  const initialForm = {
    nama: "",
    no_hp: "",
    email: "",
    alamat: "",
    area: "Dago",
    lat: -6.8732,
    lng: 107.6190,
    tipe_koneksi: "PPPoE",
    username_pppoe: "",
    password_pppoe: "",
    ip_address: "10.10.20.110",
    paket_id: "",
    odp_id: "",
    router_id: "ROUTER-UTAMA-CCR2004",
    onu_sn: "ZTEGC" + Math.random().toString(36).substring(2, 8).toUpperCase(),
    optical_power: -20.5,
    status_layanan: "aktif",
    tanggal_pasang: new Date().toISOString().split("T")[0],
    jatuh_tempo_hari: 10
  };
  const [formData, setFormData] = useState(initialForm);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resPel, resPak, resOdp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pelanggan`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/paket`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/odp`, { withCredentials: true })
      ]);
      setPelangganList(resPel.data);
      setMasterPaket(resPak.data);
      setMasterOdp(resOdp.data);
      if (resPak.data.length > 0 && !formData.paket_id) {
        setFormData((prev) => ({
          ...prev,
          paket_id: resPak.data[0].id || resPak.data[0]._id
        }));
      }
      if (resOdp.data.length > 0 && !formData.odp_id) {
        setFormData((prev) => ({
          ...prev,
          odp_id: resOdp.data[0].id || resOdp.data[0]._id
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    try {
      if (editCustomer) {
        await axios.put(
          `${BACKEND_URL}/api/pelanggan/${editCustomer.id}`,
          formData,
          { withCredentials: true }
        );
        setNotification("Data pelanggan berhasil diperbarui!");
      } else {
        await axios.post(`${BACKEND_URL}/api/pelanggan`, formData, {
          withCredentials: true
        });
        setNotification("Pelanggan baru berhasil didaftarkan ke sistem & Mikrotik!");
      }
      setShowAddModal(false);
      setEditCustomer(null);
      setFormData(initialForm);
      fetchData();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menyimpan data pelanggan");
    }
  };

  const handleChangeStatus = async (customer, newStatus) => {
    if (newStatus === customer.status_layanan) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/pelanggan/${customer.id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      setNotification(
        `Status ${customer.nama} berhasil diubah menjadi: ${newStatus.toUpperCase()}`
      );
      fetchData();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal mengubah status");
    }
  };

  const handleDelete = async (id, nama) => {
    if (window.confirm(`Yakin ingin menghapus pelanggan "${nama}"?`)) {
      try {
        await axios.delete(`${BACKEND_URL}/api/pelanggan/${id}`, {
          withCredentials: true
        });
        setNotification("Pelanggan berhasil dihapus");
        fetchData();
        setTimeout(() => setNotification(""), 4000);
      } catch (err) {
        alert("Gagal menghapus pelanggan");
      }
    }
  };

  const handleOpenEdit = (customer) => {
    setEditCustomer(customer);
    setFormData({
      ...customer,
      paket_id: customer.paket_id || "",
      odp_id: customer.odp_id || ""
    });
    setShowAddModal(true);
  };

  const handleOpenDetail = async (customer) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/pelanggan/${customer.id}`,
        { withCredentials: true }
      );
      setDetailCustomer(res.data);
    } catch (err) {
      setDetailCustomer(customer);
    }
  };

  const filteredList = pelangganList.filter((c) => {
    const matchesSearch =
      c.nama?.toLowerCase().includes(search.toLowerCase()) ||
      c.no_layanan?.toLowerCase().includes(search.toLowerCase()) ||
      c.no_hp?.includes(search) ||
      c.username_pppoe?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      filterStatus === "ALL" || c.status_layanan === filterStatus;
    const matchesArea = filterArea === "ALL" || c.area === filterArea;

    return matchesSearch && matchesStatus && matchesArea;
  });

  const totalAktif = pelangganList.filter((c) => c.status_layanan === "aktif").length;
  const totalNonAktif = pelangganList.length - totalAktif;
  const totalPages = Math.max(1, Math.ceil(filteredList.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pagedList = filteredList.slice((safePage - 1) * perPage, safePage * perPage);

  const areas = Array.from(new Set(pelangganList.map((c) => c.area).filter(Boolean)));

  useEffect(() => { setPage(1); }, [search, filterStatus, filterArea, perPage]);

  return (
    <div className="space-y-6" data-testid="pelanggan-page">
      <UpgradeRequestsPanel onChanged={fetchData} />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
              Customer Directory
            </span>
            <span className="text-xs text-slate-400">Total {pelangganList.length} Pelanggan</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">
            Data Pelanggan Internet
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Kelola profil pelanggan, paket langganan, username PPPoE, koordinat ODP & riwayat tagihan.
          </p>
        </div>

        {canAdd && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            data-testid="import-pelanggan-btn"
            className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2 transition-all"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Import Excel</span>
          </button>
          <button
            onClick={() => {
              setEditCustomer(null);
              setFormData({
                ...initialForm,
                paket_id: masterPaket[0]?.id || "",
                odp_id: masterOdp[0]?.id || ""
              });
              setShowAddModal(true);
            }}
            data-testid="add-pelanggan-btn"
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Tambah Pelanggan Baru</span>
          </button>
        </div>
        )}
      </div>

      {notification && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Kartu Ringkasan Status */}
      <div className="grid grid-cols-3 gap-3">
        <div className="px-4 py-3 rounded-xl bg-[#0e1628] border border-slate-800 shadow-lg flex items-center gap-3" data-testid="card-total-pelanggan">
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 flex-shrink-0"></span>
          <div>
            <p className="text-lg font-black text-white leading-none">{pelangganList.length}</p>
            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Total Pelanggan</p>
          </div>
        </div>
        <div className="px-4 py-3 rounded-xl bg-[#0e1628] border border-emerald-500/30 shadow-lg flex items-center gap-3" data-testid="card-pelanggan-aktif">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
          <div>
            <p className="text-lg font-black text-emerald-400 leading-none">{totalAktif}</p>
            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Aktif</p>
          </div>
        </div>
        <div className="px-4 py-3 rounded-xl bg-[#0e1628] border border-rose-500/30 shadow-lg flex items-center gap-3" data-testid="card-pelanggan-nonaktif">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400 flex-shrink-0"></span>
          <div>
            <p className="text-lg font-black text-rose-400 leading-none">{totalNonAktif}</p>
            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Non-Aktif / Isolir</p>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            data-testid="search-pelanggan-input"
            placeholder="Cari nama, ID pelanggan, PPPoE user, No HP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="ALL">Semua Status</option>
            <option value="aktif">Aktif</option>
            <option value="isolir">Isolir</option>
            <option value="nonaktif">Nonaktif</option>
          </select>

          {/* Area Filter */}
          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="ALL">Semua Area</option>
            {areas.map((a) => (
              <option key={a} value={a}>{"Area: " + a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="pelanggan-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                <th className="py-3 px-3">No. Layanan & Nama</th>
                <th className="py-3 px-3">Kontak / WhatsApp</th>
                <th className="py-3 px-3">Paket & Tarif</th>
                <th className="py-3 px-3">ODP & Redaman</th>
                <th className="py-3 px-3">Kredensial PPPoE</th>
                <th className="py-3 px-3 text-center">Status</th>
                <th className="py-3 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {pagedList.map((cust) => (
                <tr key={cust.id} className="hover:bg-slate-900/40 transition-colors">
                  <td className="py-3 px-3">
                    <p className="font-bold text-white text-sm">{cust.nama}</p>
                    <span className="text-[11px] font-mono text-cyan-400 font-semibold">
                      {cust.no_layanan}
                    </span>
                    <p className="text-[10px] text-slate-400 truncate max-w-xs mt-0.5">
                      {cust.alamat}
                    </p>
                  </td>

                  <td className="py-3 px-3">
                    <p className="text-slate-200 font-medium">{cust.no_hp}</p>
                    {cust.no_hp && (
                      <a
                        href={`https://wa.me/62${cust.no_hp.replace(/^0/, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] text-emerald-400 hover:underline mt-0.5"
                      >
                        <Phone className="w-2.5 h-2.5" />
                        <span>Chat WA</span>
                      </a>
                    )}
                  </td>

                  <td className="py-3 px-3">
                    <p className="font-semibold text-slate-200">{cust.paket_nama}</p>
                    <span className="text-[11px] font-mono text-emerald-400 font-bold">
                      Rp {Number(cust.paket_harga || 0).toLocaleString("id-ID")}
                    </span>
                  </td>

                  <td className="py-3 px-3">
                    <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold text-[10px]">
                      {cust.odp_kode || "ODP-BDG-01"}
                    </span>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                      Rx: {cust.optical_power} dBm
                    </p>
                  </td>

                  <td className="py-3 px-3">
                    <p className="font-mono text-slate-300 text-[11px]">
                      User: <span className="text-cyan-300">{cust.username_pppoe}</span>
                    </p>
                    <p className="font-mono text-slate-500 text-[10px]">
                      IP: {cust.ip_address}
                    </p>
                  </td>

                  <td className="py-3 px-3 text-center">
                    {canEdit ? (
                      <select
                        value={cust.status_layanan}
                        onChange={(e) => handleChangeStatus(cust, e.target.value)}
                        data-testid={`status-select-${cust.id}`}
                        title="Ubah status pelanggan"
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase cursor-pointer border focus:outline-none appearance-auto ${
                          cust.status_layanan === "aktif"
                            ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                            : cust.status_layanan === "isolir"
                            ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
                            : "bg-rose-500/20 text-rose-300 border-rose-500/30"
                        }`}
                      >
                        <option value="aktif">Aktif</option>
                        <option value="nonaktif">Non Aktif</option>
                        <option value="isolir">Isolir</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          cust.status_layanan === "aktif"
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                        }`}
                      >
                        {cust.status_layanan}
                      </span>
                    )}
                  </td>

                  <td className="py-3 px-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => handleOpenDetail(cust)}
                        title="Lihat Detail & Riwayat Tagihan"
                        data-testid={`detail-pelanggan-${cust.id}`}
                        className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>

                      {!canEdit ? null : (
                      <button
                        onClick={() => handleChangeStatus(cust, cust.status_layanan === "aktif" ? "isolir" : "aktif")}
                        title={cust.status_layanan === "aktif" ? "Isolir" : "Buka Isolir"}
                        data-testid={`toggle-status-${cust.id}`}
                        className={`p-1.5 rounded-lg transition-colors ${
                          cust.status_layanan === "aktif"
                            ? "bg-rose-950/60 hover:bg-rose-900 text-rose-300"
                            : "bg-emerald-950/60 hover:bg-emerald-900 text-emerald-300"
                        }`}
                      >
                        {cust.status_layanan === "aktif" ? (
                          <Lock className="w-3.5 h-3.5" />
                        ) : (
                          <Unlock className="w-3.5 h-3.5" />
                        )}
                      </button>
                      )}

                      {!canEdit ? null : (
                      <button
                        onClick={() => setTundaCustomer(cust)}
                        title={cust.tunda_isolir_sampai ? `Isolir ditunda s/d ${cust.tunda_isolir_sampai}` : "Tunda isolir otomatis"}
                        data-testid={`tunda-isolir-${cust.id}`}
                        className={`p-1.5 rounded-lg transition-colors ${cust.tunda_isolir_sampai ? "bg-amber-500/25 text-amber-300 ring-1 ring-amber-500/50" : "bg-slate-800 hover:bg-slate-700 text-slate-400"}`}
                      >
                        <CalendarClock className="w-3.5 h-3.5" />
                      </button>
                      )}

                      {!canEdit ? null : (
                      <button
                        onClick={() => handleOpenEdit(cust)}
                        title="Edit Data"
                        data-testid={`edit-pelanggan-${cust.id}`}
                        className="p-1.5 bg-slate-800 hover:bg-slate-700 text-cyan-400 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      )}

                      {!canEdit ? null : (
                      <button
                        onClick={() => handleDelete(cust.id, cust.nama)}
                        title="Hapus"
                        data-testid={`delete-pelanggan-${cust.id}`}
                        className="p-1.5 bg-slate-800 hover:bg-rose-950 text-rose-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-slate-800" data-testid="pelanggan-pagination">
          <span className="text-[11px] text-slate-400">
            Menampilkan {filteredList.length === 0 ? 0 : (safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, filteredList.length)} dari {filteredList.length} pelanggan
          </span>
          <div className="flex items-center gap-2">
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              data-testid="per-page-select"
              className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-[11px] text-slate-200"
            >
              <option value={10}>10 / halaman</option>
              <option value={20}>20 / halaman</option>
            </select>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              data-testid="prev-page-btn"
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg text-[11px] font-bold text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &lsaquo; Sebelumnya
            </button>
            <span className="text-[11px] text-slate-400" data-testid="page-indicator">Hal {safePage} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              data-testid="next-page-btn"
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg text-[11px] font-bold text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Selanjutnya &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* Import Pelanggan Modal */}
      {showImportModal && (
        <ImportPelangganModal
          onClose={() => setShowImportModal(false)}
          onSuccess={(data) => {
            setNotification(data.message || "Import pelanggan selesai!");
            setTimeout(() => setNotification(""), 5000);
            fetchData();
          }}
        />
      )}

      {/* Add / Edit Customer Modal (New 2-Tab Form) */}
      {showAddModal && (
        <AddPelangganModal
          initial={editCustomer}
          onClose={() => { setShowAddModal(false); setEditCustomer(null); }}
          onSuccess={(data) => {
            setNotification(data.message || "Pelanggan berhasil disimpan!");
            setTimeout(() => setNotification(""), 4000);
            fetchData?.();
          }}
        />
      )}

      {/* Customer Profile & Past Invoices Modal */}
      {tundaCustomer && (
        <TundaIsolirDialog pelanggan={tundaCustomer} onClose={() => setTundaCustomer(null)} onSaved={fetchData} />
      )}

      {detailCustomer && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#090f1d]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold">
                  {detailCustomer.nama?.[0] || "C"}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {detailCustomer.nama}
                  </h3>
                  <p className="text-xs font-mono text-cyan-400">
                    {detailCustomer.no_layanan}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDetailCustomer(null)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 text-xs">
              {/* Grid detail */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl">
                  <span className="text-[10px] text-slate-400 block">Paket Langganan</span>
                  <span className="font-bold text-white text-sm">
                    {detailCustomer.paket_nama}
                  </span>
                </div>
                <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl">
                  <span className="text-[10px] text-slate-400 block">ODP Terhubung</span>
                  <span className="font-bold text-amber-400 text-sm">
                    {detailCustomer.odp_kode || "ODP-BDG-01"}
                  </span>
                </div>
                <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl">
                  <span className="text-[10px] text-slate-400 block">Redaman Kabel Optik</span>
                  <span className="font-bold text-cyan-400 font-mono text-sm">
                    {detailCustomer.optical_power} dBm
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-white text-xs mb-2">Foto Rumah & ODP</h4>
                <PelangganFotoPanel pelangganId={detailCustomer.id} />
              </div>

              {/* Invoices history table */}
              <div>
                <h4 className="font-bold text-white text-xs mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-amber-400" />
                  <span>Riwayat Tagihan & Pembayaran</span>
                </h4>
                <div className="border border-slate-800 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-900 border-b border-slate-800 text-slate-400">
                        <th className="py-2 px-3">No. Invoice</th>
                        <th className="py-2 px-3">Periode</th>
                        <th className="py-2 px-3">Nominal</th>
                        <th className="py-2 px-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {(detailCustomer.invoices || []).map((inv) => (
                        <tr key={inv.id}>
                          <td className="py-2 px-3 font-mono text-slate-300">
                            {inv.no_invoice}
                          </td>
                          <td className="py-2 px-3 text-slate-300">
                            {inv.periode_bulan}
                          </td>
                          <td className="py-2 px-3 font-bold text-white">
                            Rp {Number(inv.total_tagihan).toLocaleString("id-ID")}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                inv.status === "PAID"
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : "bg-rose-500/20 text-rose-300"
                              }`}
                            >
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
